/* eslint-disable no-undef */
import type {
  Segment,
  SegmentCollection,
  SegmentFragment,
  SegmentTransition,
  SegmentVideo,
  Track,
} from '@ve-pro/types/track.type'
import { getMainTrack, useTrackStore } from '.'
import { SegmentType } from '@ve-pro/enums/segment'
import {
  createAndAddDefaultModeMainTrack,
  createTrack,
  getConflictSegment,
} from './add-segment-action'
import { TrackType, TrackMode } from '@ve-pro/enums/track-type'
import {
  hasSegmentMaterial,
  resolveSegmentFragment,
  resolveSegmentProperties,
} from './resolve-segment'
import { generateSegmentIdFromService, generateTrackId } from './generate-id'
import { useProjectStore } from '../project'
import { setActiveSegment } from './toggle-active-segment'
import {
  addSegment,
  getTracksMaxDuration,
  insertAfterTrack,
  insertBeforeTrack,
  remove,
  removeSegment,
  removeTrackIfEmptySync,
} from './tracks-actions'
import { getSortedSegments, updateMainTrack } from './update-main-track'
import { cloneSegment } from './segment/patch'
import {
  isSegmentFragment,
  isSegmentFragmentChild,
} from './segment/segment-fragment'
import {
  SupportTransitionSegment,
  getTransition,
  isSupportTransition,
  isTransition,
  removeTransition,
  updateTransitionRelevance,
} from './segment/segment-transition'
import { add, minus } from '@com/utils/math'
import { reactive } from 'vue'
import { isSegmentGroup, isSegmentGroupChild } from './segment/segment-group'
import { initTracks } from './init'
import { getFragment } from '@ve-pro/api/aside'
import { copyFragmentSegments } from '@ve-pro/api/fragment/index'
import Vue from 'vue'
import type { SegmentFragmentsResource } from '@ve-pro/api/template/type'
import { durationToFrames } from './frame'
import type { VideoTemplateItemType } from '@pt/api/template/index.type'
import { addToProject } from '@ve-pro/api/template'
import { Message } from 'element-ui'
import { t } from '@ve-pro/ve/lang'
import { AssetType } from '@ve-pro/enums/asset'
import { cloneDeep } from 'lodash-es'
import type { ProjectConfig, SpeakerCollection } from '@ve-pro/api/project/type'
import { getConfigsSpeaker } from '../project/config'
import {
  resolveFullLanguageSpeakerConfig,
  resolveSpeakerValue,
} from '../speaker/utils'
import { resolveTracksSpeaker } from './init.ve'
import { getSpeechVoiceList } from '@/api/ai/speech'
import { SpeechVoiceModel } from '@/api/ai/index.type'

/**
 * 设置元件添加到片段后到时间
 */
export function resolveFragmentTracksSegmentDuration(
  segment: SegmentCollection,
): void {
  const trackStore = useTrackStore()
  if (isSegmentFragmentChild(segment)) {
    const fragmentSegment = trackStore.defaultFragments.find(
      (f) => f.id === segment.fragment_segment_id,
    )
    if (
      fragmentSegment &&
      !(
        (segment.type === SegmentType.Video &&
          segment.material.type === AssetType.Video) ||
        segment.type === SegmentType.Audio
      )
    ) {
      // eslint-disable-next-line no-nested-ternary
      const duration = hasSegmentMaterial(segment)
        ? segment.material?.duration
          ? Math.min(fragmentSegment.duration, segment.material.duration)
          : fragmentSegment.duration
        : fragmentSegment.duration

      segment.duration = duration
      segment.duration_frames = durationToFrames(duration)
    }
  }
}

/**
 * 添加片段
 * 特性：
 * 1. 调用必须指定片段里面的剧本（剧本包含元件和轨道两者完整的关联关系）
 * 2. 轨道会重新创建
 * 3. 轨道里面的元件id不调整，由调用层确定
 *
 * 适应场景 examples：
 *
 * 1. 添加空白片段:
 * addSegmentFragment([])
 *
 * @param rawTracks { Track[] } 原始片段剧本，具备完整的关系，但是需要重新建立关系「轨道id」
 * @returns { Promise<void> }
 */
export async function addSegmentFragment(
  rawTracks: Track[],
  fromFragment?: SegmentFragment,
): Promise<SegmentFragment> {
  const trackStore = useTrackStore()
  // 创建片段元件
  const fragment = resolveSegmentFragment()
  const fragmentSegmentId = await generateSegmentIdFromService(fragment)
  fragment.id = fragmentSegmentId
  fragment.start_time = trackStore.totalTime
  if (fromFragment) {
    fragment.duration = fromFragment.duration
    fragment.duration_frames = fromFragment.duration_frames
  }

  // 解除在原本轨道的关系，并单独创建相对位置的轨道进行存放
  const tracks = rawTracks.map<Track>((t) => {
    const trackId = generateTrackId()

    // 关联片段元件和children的关系
    t.segments.forEach((s) => {
      s.fragment_segment_id = fragmentSegmentId
      s.track_id = trackId
    })

    return reactive({
      ...t,
      id: trackId,
      fragment_segment_id: fragmentSegmentId,
      // 段集合
      segments: [...(t.segments as any)],
    })
  })

  tracks.forEach((t) => {
    const segments = [...t.segments]
    segments.forEach((s) => {
      // 子元件
      if (isSegmentGroupChild(s)) {
        let children = trackStore.segmentGroups[s.pid]
        if (!children) {
          Vue.set(trackStore.segmentGroups, s.pid, [])

          children = trackStore.segmentGroups[s.pid]
        }

        remove(s, t.segments as any)
        s.start_frame = 0
        children?.push(s)
      }
    })
  })

  addToTracks(tracks)

  const newTrackMain = getMainTrack(tracks)
  if (newTrackMain) {
    updateTransitionRelevance(newTrackMain)
    updateMainTrack(true, {
      mainTrack: newTrackMain,
    })
  }

  const mainTrack = trackStore.createDefaultModeTrackMainIfNotExist()
  resolveSegmentProperties(fragment)
  addSegment(fragment, mainTrack)
  setActiveSegment(fragment)
  updateMainTrack(true, {
    mainTrack,
  })

  return fragment
}

/**
 * 设为片段
 * @description 选一个或者多个元件右键“设为片段” 指定的元件是存在轨道的，保证能获取轨道信息
 * @param segmentIds 轨道选中的元件id
 * @returns
 */
export async function toSegmentFragmentFromSelectedSegments(
  segmentIds: Segment['id'][],
): Promise<Awaited<ReturnType<typeof addSegmentFragment>>> {
  const trackStore = useTrackStore()

  // 创建好的片段剧本
  const fragmentTracks: Track[] = []
  const allTracks = [...trackStore.tracks]
  for (let index = 0; index < allTracks.length; index++) {
    const track = allTracks[index]!
    const segments = track.segments.filter((segment) => {
      return (
        segmentIds.includes(segment.id) && segment.type !== SegmentType.Fragment
      )
    })

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i]!
      await removeSegment(segment)
    }

    if (segments.length) {
      fragmentTracks.push({
        ...track,
        segments: segments as any,
      })
    }
  }

  const startFrame = fragmentTracks.reduce<number>((v, t) => {
    let min = v
    for (let index = 0; index < t.segments.length; index++) {
      const segment = t.segments[index]!
      min = Math.min(min, segment.start_frame)
    }

    return min
  }, Infinity)

  fragmentTracks.forEach((t) => {
    t.segments.forEach((s) => {
      s.start_frame = minus(s.start_frame, startFrame)
    })
  })

  let mainTrack = fragmentTracks.find((t) => t.type === TrackType.Video)
  if (!mainTrack) {
    mainTrack = createTrack(TrackType.Video)
    fragmentTracks.unshift(mainTrack)
  }
  mainTrack.is_main = true

  return addSegmentFragment(fragmentTracks)
}

/**
 * 复制轨道的片段
 * @param target { SegmentFragment } 轨道上选中的片段
 * @returns { void }
 */
export async function copySegmentFragment(
  target: SegmentFragment,
): Promise<Awaited<ReturnType<typeof addSegmentFragment>>> {
  const projectStore = useProjectStore()
  const trackStore = useTrackStore()

  const fragmentSegmentId = target.id

  const sourceSegments = trackStore.segments.filter(
    (s) => s.fragment_segment_id === fragmentSegmentId,
  )

  if (sourceSegments.length === 0) {
    return addSegmentFragment([], target)
  }

  const segmentCount = sourceSegments.reduce((count, segment) => {
    if (isSegmentGroup(segment)) {
      const children = trackStore.segmentGroups[segment.id] ?? []
      return count + 1 + children.length
    }

    return count + 1
  }, 0)

  const res = await copyFragmentSegments({
    project_id: projectStore.id,
    count: segmentCount,
  })

  const ids = (res.data.data.segments ?? []).map((s) => s.id)

  // 创建好的片段剧本
  const fragmentTracks: Track[] = reactive([])
  const excludeFragmentSegment = <T extends SegmentCollection>(segments: T[]) =>
    segments.filter((segment) => !isSegmentFragment(segment)) as Exclude<
      T,
      SegmentFragment
    >[]

  const transitionMap: Record<SegmentTransition['id'], SegmentTransition> = {}
  trackStore.tracks.forEach((track) => {
    const segments = excludeFragmentSegment(track.segments as any)
      .filter((segment) => segment.fragment_segment_id === fragmentSegmentId)
      .reduce<SegmentCollection[]>((segments, s) => {
        const pid = ids.shift()
        if (!pid) {
          console.error(`片段剧本${track.id}中，片段${s.name}的id为空`)
        }

        if (isSegmentGroup(s)) {
          const children = trackStore.segmentGroups[s.id] ?? []
          children.forEach((s) => {
            const child = cloneSegment(s)
            const childId = ids.shift() ?? child.id
            child.id = childId
            child.pid = pid ?? child.pid
            segments.push(child)
          })
        }
        const copied = cloneSegment(s, { keepTransition: true })
        const oldId = s.id

        copied.id = pid ?? copied.id
        segments.push(copied)

        if (isTransition(copied)) {
          transitionMap[oldId] = copied
        }
        return segments
      }, [])

    if (segments.length) {
      segments.forEach((segment) => {
        if (isSupportTransition(segment)) {
          if (segment.before_transition_id) {
            segment.before_transition_id =
              transitionMap[segment.before_transition_id]?.id ??
              segment.before_transition_id
          }

          if (segment.behind_transition_id) {
            segment.behind_transition_id =
              transitionMap[segment.behind_transition_id]?.id ??
              segment.behind_transition_id
          }
        }
      })
      fragmentTracks.push({
        ...track,
        segments: segments as any,
      })
    }
  })

  const task = addSegmentFragment(fragmentTracks, target)
  task.then((f) => {
    const mainTrack = trackStore.getMainTrack(
      trackStore.getTracksByFragmentSegmentId(f.id),
    )
    if (mainTrack) {
      updateTransitionRelevance(mainTrack)
      updateMainTrack(true, { mainTrack })
    }
  })
  return task
}

/**
 * 从公共视频模版库选择添加“片段”
 * @param template 模版数据
 * @param options.shouldCover { boolean } 是否覆盖当前项目
 * @param options {}
 */
export async function addSegmentFragmentFromTemplate(
  template: VideoTemplateItemType,
): Promise<void> {
  const projectStore = useProjectStore()
  const trackStore = useTrackStore()

  const res = await addToProject({
    id: template.project_id,
    dest_id: projectStore.id,
  })
  await projectStore.updateAssetGroup()
  const project = res.data.data
  const { tracks, segmentGroups, configs } =
    initTracks(project, {
      assetGroups: projectStore.assetGroups,
      defaultFont: projectStore.defaultFont!,
      newTrackId: true,
    }) ?? {}

  await mergeProjectConfigSpeaker(projectStore.configs.speaker, {
    tracks,
    segmentGroups,
    configs,
  })

  if (segmentGroups) {
    Object.keys(segmentGroups).forEach((pid) => {
      Vue.set(trackStore.segmentGroups, pid, segmentGroups[pid as any])
    })
  }

  if (!tracks) {
    Message.error(t('fragment.action.add.fromEmptyTemplate'))
    return Promise.reject()
  }

  const currentTrackMode = trackStore.mode
  trackStore.mode = project.project?.track_mode ?? TrackMode.Tradition
  const templateTracks = [...trackStore.templateTracks]
  trackStore.tracks = [...templateTracks, ...tracks]

  setTrackMode(currentTrackMode)

  if (currentTrackMode === TrackMode.Fragment) {
    trackStore.defaultFragments.forEach((s) => {
      s.fragment_id = 0
    })
  }
}
/**
 * 从公共视频模版库选择添加“片段”
 * 参考技术文档：https://confluence.mobvista.com/pages/viewpage.action?pageId=94007282#id-%E3%80%90%E5%89%8D%E7%AB%AF%E3%80%91%E8%A7%86%E9%A2%91%E5%86%85%E5%AE%B9%E6%A8%A1%E7%89%88-%E4%BB%8E%E5%85%AC%E5%85%B1%E8%A7%86%E9%A2%91%E6%A8%A1%E7%89%88%E5%BA%93%E9%80%89%E6%8B%A9%E6%B7%BB%E5%8A%A0%E2%80%9C%E7%89%87%E6%AE%B5%E2%80%9D
 * @param fragment {  }片段详情
 */
export async function addSegmentFragmentFromFragment(
  fragmentId: SegmentFragmentsResource['fragment_id'],
): Promise<void> {
  const trackStore = useTrackStore()
  const projectStore = useProjectStore()

  const res = await getFragment({
    fragment_id: fragmentId,
    project_id: projectStore.id,
  })

  await projectStore.updateAssetGroup()

  const { tracks, segmentGroups, configs } =
    initTracks(res.data.data, {
      assetGroups: projectStore.assetGroups,
      defaultFont: projectStore.defaultFont!,
      newTrackId: true,
    }) ?? {}

  await mergeProjectConfigSpeaker(projectStore.configs.speaker, {
    tracks,
    segmentGroups,
    configs,
  })

  if (segmentGroups) {
    Object.keys(segmentGroups).forEach((pid) => {
      Vue.set(trackStore.segmentGroups, pid, segmentGroups[pid as any])
    })
  }

  if (tracks) {
    const segments = tracks.reduce<SegmentCollection[]>((acc, track) => {
      return acc.concat(...track.segments)
    }, [])

    let fragmentSegment = segments.find(isSegmentFragment)

    if (!fragmentSegment) {
      fragmentSegment = resolveSegmentFragment()

      const fragment_segment_id = segments.find((s) =>
        isSegmentFragmentChild(s),
      )?.fragment_segment_id
      fragmentSegment.id = fragment_segment_id ?? 0
    }

    const fragmentTrack = tracks.find((t) => {
      return t.segments.some((s) => s.id === fragmentSegment?.id)
    })!
    remove(fragmentTrack, tracks)

    const mainTrack = trackStore.createDefaultModeTrackMainIfNotExist()
    if (trackStore.mode === TrackMode.Tradition) {
      const totalFrame = durationToFrames(
        trackStore.defaultExcludeFragmentMainTrack
          ? getTracksMaxDuration([trackStore.defaultExcludeFragmentMainTrack])
          : 0,
      )
      tracks.forEach((t) => {
        t.fragment_segment_id = 0
        t.segments.forEach((s) => {
          s.fragment_segment_id = 0
          s.start_frame = totalFrame + s.start_frame
        })
      })

      mergeToStoreTracks(trackStore.defaultExcludeFragmentTracks, tracks)

      setActiveSegment(
        segments.find((s) => s.type === SegmentType.Video),
        segments.filter((s) => !isSegmentFragment(s)).map((s) => s.id),
      )
      updateMainTrack(true, {
        mainTrack,
      })
    } else if (trackStore.mode === TrackMode.Fragment) {
      fragmentSegment.start_time = trackStore.totalTime

      addSegment(fragmentSegment!, mainTrack)
      setActiveSegment(fragmentSegment)
      updateMainTrack(true, {
        mainTrack,
      })

      // 设置相对片段开始时间
      const fragmentSegmentStartFrame = fragmentSegment.start_frame
      tracks.forEach((t) => {
        t.segments.forEach((_s) => {
          if (!isSegmentFragmentChild(_s)) {
            const s = _s as SegmentCollection
            s.start_frame = add(fragmentSegmentStartFrame, s.start_frame)
          }
        })
      })
      addToTracks(tracks)
    }
  }
}

export function toggleTrackMode(): void {
  const trackStore = useTrackStore()
  setTrackMode(
    trackStore.mode === TrackMode.Tradition
      ? TrackMode.Fragment
      : TrackMode.Tradition,
  )
}
export function setTrackMode(mode: TrackMode): void {
  if (mode === TrackMode.Fragment) {
    toFragmentTracks()
  } else if (mode === TrackMode.Tradition) {
    toTraditionTracks()
  }
}

function toTraditionTracks(): void {
  const trackStore = useTrackStore()

  if (trackStore.mode === TrackMode.Tradition) {
    return
  }
  // 收敛所有的片段轨道，层级尽可能靠近主轨道
  const defaultMainTrack = trackStore.defaultExcludeFragmentMainTrack

  if (defaultMainTrack) {
    const sortedFragmentSegments = getSortedSegments({
      track: defaultMainTrack,
    }) as SegmentFragment[]

    trackStore.createDefaultModeTrackMainIfNotExist()

    const defaultMainTrackIndex = trackStore.tracks.findIndex(
      (t) => t.id === defaultMainTrack.id,
    )

    const mergedTracks = flattenFragments(sortedFragmentSegments)
    if (mergedTracks.length) {
      trackStore.tracks.splice(defaultMainTrackIndex, 1, ...mergedTracks)
    }
  }

  trackStore.mode = TrackMode.Tradition
}

function removeTrackTransition(
  mainTrack: Track<TrackType.Video>,
  segments?: SupportTransitionSegment[],
) {
  segments = segments ?? mainTrack.segments.filter(isSupportTransition)
  segments.forEach((s) => {
    const { before, behind } = getTransition(s)
    if (before) {
      removeTransition(before)
    }
    if (behind) {
      removeTransition(behind)
    }
  })
  updateMainTrack(true, {
    mainTrack: mainTrack,
  })
}

/**
 * 打平片段轨道（具有副作用）
 * @param sortedFragmentSegments
 * @returns
 */
function flattenFragments(
  sortedFragmentSegments: SegmentFragment[],
  options?: { removeTransiton?: boolean },
): Track[] {
  const trackStore = useTrackStore()
  // 收敛所有的片段轨道，层级尽可能靠近主轨道
  const defaultMainTrack = trackStore.defaultExcludeFragmentMainTrack

  if (!defaultMainTrack) {
    return []
  }

  // 片段模式下的片段轨道，先删除转场
  removeTrackTransition(defaultMainTrack, sortedFragmentSegments)

  /**
   * 合并所有轨道
   * 参考 trackStore.tracks 数组，轨道排序如下（与视觉看上去完全相反）：
   * 从上至下：音频轨道 -> 视频轨道 -> 其他轨道
   *
   * 合并逻辑：按片段内的相对进场时间+片段进场时间计算最终的进场时间，然后按现有轨道的规则，能放得下就放，放不下就向下另起一个轨道。
   *
   * 合并后，要保留的特性：
   * 1. 轨道的静音、是否展示
   * 2. 轨道的相对位置不能破坏
   * 3. 所有的片段里面的轨道，必须尽可能 靠近主轨道
   */
  const cachedFragmentSegmentStartFrame: Record<
    SegmentFragment['id'],
    SegmentFragment['start_frame']
  > = {}
  sortedFragmentSegments.forEach(
    (f) => (cachedFragmentSegmentStartFrame[f.id] = f.start_frame),
  )
  const mergedTracks = sortedFragmentSegments.reduce<Track[]>(
    (mergedTracks, fragmentSegment) => {
      const tracks = trackStore.getTracksByFragmentSegmentId(fragmentSegment.id)
      const currentMainTrack = getMainTrack(tracks)
      if (currentMainTrack && options?.removeTransiton) {
        removeTrackTransition(currentMainTrack)
      }

      tracks.forEach((track) => {
        track.segments.forEach((s) => {
          if (!isTransition(s)) {
            s.start_frame = add(
              cachedFragmentSegmentStartFrame[fragmentSegment.id] ??
                fragmentSegment.start_frame,
              s.start_frame,
            )
          }
        })
      })

      remove(fragmentSegment, defaultMainTrack.segments)
      trackStore.tracks
        .filter((t) => t.fragment_segment_id === fragmentSegment.id)
        .forEach((t) => {
          remove(t, trackStore.tracks)
        })

      if (tracks.length === 0) {
        return mergedTracks
      }

      const mainTrack = trackStore.getMainTrack(tracks)
      if (!mainTrack) {
        return mergedTracks
      }

      const baseMainTrack = trackStore.getMainTrack(mergedTracks)
      if (!baseMainTrack || mergedTracks.length === 0) {
        return [...tracks]
      }

      const mainTrackIndex = tracks.findIndex(
        (track) => track.id === mainTrack.id,
      )

      // 主轨道
      mainTrack.segments.forEach((s) => {
        addSegment(s, baseMainTrack)
      })

      // 主轨道下方
      let currentStartMergedAudioTrackIndex = mainTrackIndex - 1
      for (let index = mainTrackIndex - 1; index >= 0; index--) {
        const t = tracks[index]!

        let currentStartMergedTrack =
          mergedTracks[currentStartMergedAudioTrackIndex]

        while (currentStartMergedTrack) {
          if (
            currentStartMergedTrack.type === t.type &&
            currentStartMergedTrack.mute === t.mute
          ) {
            // 相同轨道类型，验证是否会重叠覆盖
            const firstSegment = [...(t.segments ?? [])].sort(
              (a, b) => a.start_time - b.start_time,
            )[0]

            if (
              firstSegment &&
              !getConflictSegment(firstSegment, currentStartMergedTrack)
            ) {
              const _currentStartMergedTrack = currentStartMergedTrack

              t.segments.forEach((s) => {
                addSegment(s, _currentStartMergedTrack)
              })

              break
            }
          }

          currentStartMergedTrack =
            mergedTracks[currentStartMergedAudioTrackIndex--]
        }

        // 命中了已经有的轨道
        if (currentStartMergedTrack) {
          currentStartMergedAudioTrackIndex--
          // eslint-disable-next-line no-continue
          continue
        }

        mergedTracks.unshift(t)
        currentStartMergedAudioTrackIndex = -1
      }

      // 主轨道上方
      const mergedMainTrackIndex = mergedTracks.findIndex(
        (track) => track.id === baseMainTrack.id,
      )

      // 当前可以开始检查插入轨道的索引
      let currentStartMergedTrackIndex = mergedMainTrackIndex + 1

      for (let index = mainTrackIndex + 1; index < tracks.length; index++) {
        const t = tracks[index]!

        let currentStartMergedTrack = mergedTracks[currentStartMergedTrackIndex]

        while (currentStartMergedTrack) {
          if (
            currentStartMergedTrack.type === t.type &&
            currentStartMergedTrack.mute === t.mute
          ) {
            // 相同轨道类型，验证是否会重叠覆盖
            const firstSegment = [...(t.segments ?? [])].sort(
              (a, b) => a.start_time - b.start_time,
            )[0]

            if (
              firstSegment &&
              !getConflictSegment(firstSegment, currentStartMergedTrack)
            ) {
              const _currentStartMergedTrack = currentStartMergedTrack
              // 相同轨道类型，合并
              t.segments.forEach((s) => {
                addSegment(s, _currentStartMergedTrack)
              })

              break
            }
          }

          currentStartMergedTrack = mergedTracks[currentStartMergedTrackIndex++]
        }

        // 命中了已经有的轨道
        if (currentStartMergedTrack) {
          currentStartMergedTrackIndex++
          // eslint-disable-next-line no-continue
          continue
        }

        // 没有命中已经有的轨道
        mergedTracks.push(t)
        currentStartMergedTrackIndex = mergedTracks.length
      }

      return mergedTracks
    },
    [],
  )

  mergedTracks.forEach((t) => {
    t.fragment_segment_id = 0

    t.segments.forEach((s) => {
      s.fragment_segment_id = 0
    })
    reactive(t)
  })

  return mergedTracks
}

/**
 * 合并轨道（具有副作用）
 * 最终操作`trackStore.tracks`
 * @param target 来源于 `trackStore.tracks` 过滤出来的独立剧本
 * @param source 独立的剧本副本，将会合并到`trackStore.tracks`里面
 */
function mergeToStoreTracks(
  baseTracks: Track[],
  sourceTracks: Track[],
  options?: {
    // 尽量紧贴主轨道
    closeToMainTrack?: boolean
  },
): void {
  const { closeToMainTrack = true } = options ?? {}
  baseTracks = [...baseTracks]
  if (sourceTracks.length === 0) {
    return
  }

  const baseMainTrack = getMainTrack(baseTracks)
  let mainTrack = getMainTrack(sourceTracks)

  if (!baseMainTrack || baseTracks.length === 0) {
    addToTracks(sourceTracks)
    return
  }

  if (!mainTrack) {
    mainTrack = createTrack(TrackType.Video)
    mainTrack.is_main = true
    sourceTracks.unshift(mainTrack)
  }

  // 主轨道
  mainTrack.segments.forEach((s) => {
    addSegment(s, baseMainTrack)
  })

  const mainTrackIndex = sourceTracks.findIndex(
    (track) => track.id === mainTrack.id,
  )

  // 主轨道下方
  let currentStartMergedAudioTrackIndex = mainTrackIndex - 1
  // 上次命中的轨道索引
  let prevMergedTargetTrack: Track | null = null
  for (let index = mainTrackIndex - 1; index >= 0; index--) {
    const t = sourceTracks[index]!

    let currentStartMergedTrack = baseTracks[currentStartMergedAudioTrackIndex]

    while (currentStartMergedTrack) {
      if (
        currentStartMergedTrack.type === t.type &&
        currentStartMergedTrack.mute === t.mute
      ) {
        // 相同轨道类型，验证是否会重叠覆盖
        const firstSegment = [...(t.segments ?? [])].sort(
          (a, b) => a.start_time - b.start_time,
        )[0]

        if (
          firstSegment &&
          !getConflictSegment(firstSegment, currentStartMergedTrack)
        ) {
          const _currentStartMergedTrack = currentStartMergedTrack

          t.segments.forEach((s) => {
            addSegment(s, _currentStartMergedTrack)
          })

          break
        }
      }

      currentStartMergedTrack = baseTracks[currentStartMergedAudioTrackIndex--]
    }

    // 命中了已经有的轨道
    if (currentStartMergedTrack) {
      prevMergedTargetTrack = currentStartMergedTrack
      currentStartMergedAudioTrackIndex--
      // eslint-disable-next-line no-continue
      continue
    }

    const targetTrack = closeToMainTrack ? prevMergedTargetTrack : baseTracks[0]
    insertBeforeTrack(t, targetTrack ?? baseMainTrack)
    prevMergedTargetTrack = t
    baseTracks.unshift(t)
    currentStartMergedAudioTrackIndex = -1
  }

  // 主轨道上方
  const mergedMainTrackIndex = baseTracks.findIndex(
    (track) => track.id === baseMainTrack.id,
  )

  // 当前可以开始检查插入轨道的索引
  let currentStartMergedTrackIndex = mergedMainTrackIndex + 1
  // 上次命中的轨道索引
  let prevTargetTrack: Track | null = null

  for (let index = mainTrackIndex + 1; index < sourceTracks.length; index++) {
    const t = sourceTracks[index]!

    let currentStartMergedTrack = baseTracks[currentStartMergedTrackIndex]

    while (currentStartMergedTrack) {
      if (
        currentStartMergedTrack.type === t.type &&
        currentStartMergedTrack.mute === t.mute
      ) {
        // 相同轨道类型，验证是否会重叠覆盖
        const firstSegment = [...(t.segments ?? [])].sort(
          (a, b) => a.start_time - b.start_time,
        )[0]

        if (
          firstSegment &&
          !getConflictSegment(firstSegment, currentStartMergedTrack)
        ) {
          const _currentStartMergedTrack = currentStartMergedTrack
          // 相同轨道类型，合并
          t.segments.forEach((s) => {
            addSegment(s, _currentStartMergedTrack)
          })

          break
        }
      }

      currentStartMergedTrack = baseTracks[currentStartMergedTrackIndex++]
    }

    // 命中了已经有的轨道
    if (currentStartMergedTrack) {
      prevTargetTrack = currentStartMergedTrack
      currentStartMergedTrackIndex++
      // eslint-disable-next-line no-continue
      continue
    }

    // 没有命中已经有的轨道
    const targetTrack = closeToMainTrack
      ? prevTargetTrack
      : baseTracks[baseTracks.length - 1]
    insertAfterTrack(t, targetTrack ?? baseMainTrack)
    prevTargetTrack = t
    baseTracks.push(t)
    currentStartMergedTrackIndex = baseTracks.length
  }
}

function toFragmentTracks() {
  const trackStore = useTrackStore()
  if (trackStore.mode === TrackMode.Fragment) {
    return
  }

  const mainTrack = trackStore.defaultExcludeFragmentMainTrack

  if (mainTrack && mainTrack.segments.length > 0) {
    const segments = [...mainTrack.segments]
    segments.forEach((segment) => {
      if (isTransition(segment)) {
        removeTransition(segment)
      } else if (isSegmentGroup(segment)) {
        segment.multi_cross = false
      }
    })

    updateMainTrack()
  }

  trackStore.mode = TrackMode.Fragment

  if (mainTrack && mainTrack.segments.length > 0) {
    createAndAddDefaultModeMainTrack()
  }
}

/**
 * 创建片段剧本
 * @param fragmentSegment 片段元件（id调用前就要创建完毕）
 * @param segments 连续性的主轨道元件（id调用前就要创建完毕）
 * @returns { Track[] }
 */
export function createFragmentTracks(
  fragmentSegment: SegmentFragment,
  segments: SegmentVideo[],
): Track[] {
  const mainTack = createFragmentTrack(fragmentSegment.id)

  mainTack.segments.push(...segments)

  segments.forEach((segment) => {
    segment.track_id = mainTack.id
    segment.fragment_segment_id = fragmentSegment.id
  })

  return [mainTack]
}

export function createFragmentTrack(
  fragment_segment_id: SegmentVideo['fragment_segment_id'],
): Track<TrackType.Video> {
  const mainTack = createTrack(TrackType.Video)
  mainTack.is_main = true
  mainTack.fragment_segment_id = fragment_segment_id

  return mainTack
}

export function addToTracks(
  tracks: ReturnType<typeof createFragmentTracks>,
): void {
  const trackStore = useTrackStore()

  trackStore.tracks.push(...tracks)
}

export async function moveDefaultTracksSegmentsInFragment(
  fragmentSegmentId: SegmentFragment['id'],
  segments: Array<SegmentCollection>,
): Promise<void> {
  const trackStore = useTrackStore()

  const fragmentSegment = trackStore.defaultFragments.find(
    (f) => f.id === fragmentSegmentId,
  )

  if (!fragmentSegment) {
    throw new Error(`fragmentSegment not found: ${fragmentSegmentId}`)
  }

  const clonedTracks = moveOutToCreateTracks(
    segments.filter((s) => !isSegmentFragment(s)),
    {
      toFragmentSegmentId: fragmentSegmentId,
    },
  )

  // 处理进场时间
  clonedTracks.forEach((track) => {
    const segments = [...track.segments]
    segments.forEach((segment) => {
      // 元件出场超出片段进场时间
      if (
        add(segment.start_frame, segment.duration_frames) <=
        fragmentSegment.start_frame
      ) {
        remove(segment, track.segments)
      } else {
        const start_frame = minus(
          segment.start_frame,
          fragmentSegment.start_frame,
        )

        // 裁剪
        if (start_frame < 0) {
          segment.start_frame = 0
          segment.trim_start_frame += Math.abs(start_frame)
          segment.duration_frames -= Math.abs(start_frame)
        } else {
          segment.start_frame = start_frame
        }
      }
    })
  })

  mergeToStoreTracks(
    trackStore.getTracksByFragmentSegmentId(fragmentSegmentId),
    clonedTracks,
    {
      closeToMainTrack: false,
    },
  )
}
export async function moveInFragment(
  fragmentSegmentId: SegmentFragment['id'],
  segments: Array<SegmentCollection>,
): Promise<void> {
  const trackStore = useTrackStore()

  const clonedTracks = moveOutToCreateTracks(
    segments.filter((s) => !isSegmentFragment(s)),
    {
      toFragmentSegmentId: fragmentSegmentId,
    },
  )

  mergeToStoreTracks(
    trackStore.getTracksByFragmentSegmentId(fragmentSegmentId),
    clonedTracks,
  )
}

export async function moveOutFragment(
  segments: Array<SegmentCollection>,
): Promise<void> {
  const trackStore = useTrackStore()

  const clonedTracks = moveOutToCreateTracks(segments)

  const mainTrack = getMainTrack(clonedTracks)

  if (mainTrack && mainTrack.segments.length > 0) {
    const mainTrackIndex = clonedTracks.findIndex((t) => t.id === mainTrack.id)
    const emptyMainTack = createTrack(TrackType.Video)
    mainTrack.is_main = false
    emptyMainTack.is_main = true
    emptyMainTack.fragment_segment_id = mainTrack.fragment_segment_id

    clonedTracks.splice(mainTrackIndex, 0, emptyMainTack)
  }

  trackStore.createDefaultModeTrackMainIfNotExist()

  mergeToStoreTracks(trackStore.defaultExcludeFragmentTracks, clonedTracks)
}

/**
 * 移出指定的元件，并创建新轨道剧本（包含主轨道）
 * 具有副作用，会删除指定的轨道元件
 * @param segments 全量剧本中存在的轨道元件
 * @returns {Promise<Track[]>}
 */
function moveOutToCreateTracks(
  segments: Array<SegmentCollection>,
  options?: {
    // 若指定了该参数，这剧本的信息会主动绑定该片段信息，否则默认按公共剧本处理
    toFragmentSegmentId?: SegmentFragment['id']
  },
): Track[] {
  const trackStore = useTrackStore()
  const tids = segments.map((s) => s.track_id)

  const fromFragmentSegmentId =
    (segments[0] && segments[0]?.fragment_segment_id) ?? 0

  const belonedTracksMainTrackId =
    fromFragmentSegmentId > 0
      ? trackStore.getMainTrack(
          trackStore.getTracksByFragmentSegmentId(fromFragmentSegmentId),
        )?.id
      : trackStore.defaultExcludeFragmentMainTrack?.id
  if (belonedTracksMainTrackId) {
    tids.push(belonedTracksMainTrackId)
  }

  const { toFragmentSegmentId = 0 } = options ?? {}
  const isToCommantTracks = toFragmentSegmentId === 0 || !toFragmentSegmentId

  const clonedTracks = trackStore.tracks
    .filter((t) => tids.includes(t.id))
    .map((t) => {
      return reactive({
        ...t,
        fragment_segment_id: isToCommantTracks ? 0 : toFragmentSegmentId,
        segments: [],
      }) as Track
    })

  clonedTracks.forEach((clonedTrack) => {
    const sourceTrack = trackStore.tracks.find((t) => t.id === clonedTrack.id)

    const inTrackSegments = segments.filter(
      (s) => s.track_id === clonedTrack.id,
    )

    if (sourceTrack) {
      if (trackStore.isMainTrackInBelongTracks(sourceTrack)) {
        inTrackSegments.forEach((segment) => {
          if (isSupportTransition(segment)) {
            const { before, behind } = getTransition(segment)
            before && removeTransition(before)
            behind && removeTransition(behind)
          }
        })

        // 保证元件是移除转场后的进场时间
        updateMainTrack(true, {
          mainTrack: sourceTrack,
        })
      }

      inTrackSegments.forEach((segment) => {
        remove(segment, sourceTrack.segments)

        if (isToCommantTracks) {
          const fragmentSegment = trackStore.defaultFragments.find(
            (f) => f.id === segment.fragment_segment_id,
          )
          if (fragmentSegment) {
            segment.start_frame = add(
              fragmentSegment.start_frame,
              segment.start_frame,
            )
          }
        }
        segment.fragment_segment_id = toFragmentSegmentId
        addSegment(segment, clonedTrack)
      })

      if (trackStore.isMainTrackInBelongTracks(sourceTrack)) {
        updateMainTrack(true, {
          mainTrack: sourceTrack,
        })
      }

      removeTrackIfEmptySync(sourceTrack)
    }
  })

  clonedTracks.forEach((track) => {
    const tid = generateTrackId()
    track.id = tid
    track.segments.forEach((segment) => {
      segment.track_id = tid
    })
  })

  return clonedTracks
}
const speakerLog = (title: string, msg: any) => {
  const debugScopeName = `spekaer conflict`

  console.log(`[${debugScopeName}] ${title}: ${msg}`)
}

async function mergeProjectConfigSpeaker(
  targetSpeakerConfig: ProjectConfig['speaker'],
  options: Partial<Awaited<ReturnType<typeof initTracks>>>,
): Promise<void> {
  const voiceList: SpeechVoiceModel[] =
    (await getSpeechVoiceList()).data.data ?? []

  const { tracks, segmentGroups, configs } = options ?? {}
  const beforeSpeakerConfig = cloneDeep(targetSpeakerConfig)
  const beforeMergedSpeakerConfig = cloneDeep(options?.configs?.speaker ?? {})

  const allSpeakerValues = getConfigsSpeaker(targetSpeakerConfig)
  let max = Math.max(
    ...allSpeakerValues.map((val: string) => Number(val.split('_')[1])),
  )

  // 获取到speaker关联的元件关系表
  const related = resolveTracksSpeaker({
    tracks,
    segmentGroups,
  })

  // 合并两个configs
  const mergeSpekaer = (
    key: keyof ProjectConfig['speaker'],
    newValue: keyof SpeakerCollection,
    oldValue: keyof SpeakerCollection,
  ) => {
    const config = resolveFullLanguageSpeakerConfig(
      newValue,
      configs?.speaker[key]?.[oldValue],
      {
        voices: voiceList,
        unsetDefualtLanguage: true,
      },
    )
    // 不存在就不处理
    if (!config) {
      return
    }
    let _speaker = targetSpeakerConfig[key]
    if (!_speaker) {
      _speaker = {}
      Vue.set(targetSpeakerConfig, key, _speaker)
    }

    _speaker[newValue] = config

    if (newValue !== oldValue) {
      related.get(oldValue)?.forEach((segment) => {
        segment.speaker = newValue
      })
    }
  }

  // 判断是否有冲突
  Object.keys(configs?.speaker ?? {}).forEach((_k) => {
    const k = Number(_k)
    Object.keys(configs?.speaker[Number(k)] ?? {}).forEach((speakerValue) => {
      if (allSpeakerValues.includes(speakerValue)) {
        // 冲突了
        const newSpeakerValue = resolveSpeakerValue(++max)

        // 根据冲突情况，重新命名
        mergeSpekaer(k, newSpeakerValue, speakerValue)
      } else {
        mergeSpekaer(k, speakerValue, speakerValue)
      }
    })
  })

  speakerLog('results', {
    beforeSpeakerConfig,
    beforeMergedSpeakerConfig: beforeMergedSpeakerConfig,
    afterSpeakerConfig: cloneDeep(targetSpeakerConfig),
  })
}
